"use client";

import type { ReactNode } from "react";
import type { LiveParticipantLine } from "@/lib/live/session-control";
import { ModerationMenu, MutedTag, type ModerationGestures } from "./moderation-menu";
import { initial as initialOf } from "./recital-card";

/**
 * Qui est dans le récital : l'interprète, puis la salle.
 *
 * # Ce qui n'y est pas
 *
 * Aucune latence. Ce que coûte chaque liaison est un outil de mise au point,
 * pas une information pour qui joue ou regarde : ces nombres vivent dans
 * l'encart « Debug » du panneau, en attendant de disparaître.
 *
 * # D'où viennent les noms
 *
 * De la route des participants du service, tirée toutes les trois secondes
 * par qui la regarde (`useLiveParticipants`) : le panneau tant qu'il est
 * ouvert, le widget de salle tant que l'interprète est là. LiveKit ne montre
 * plus les spectateurs, invisibles depuis le chantier bande passante : c'est
 * le service qui sait qui est là. Dans un récital, seul l'interprète peut
 * jouer, et c'est ce droit qui le distingue de la salle.
 *
 * # La modération, chez l'interprète
 *
 * Avec `moderation`, chaque ligne de la salle porte le menu — retirer la
 * parole, exclure — et la marque « sourdine » de qui l'a perdue. La marque
 * vient du relevé, que le service ne remplit qu'à qui pilote ; `mutedOverride`
 * la devance du geste qu'on vient de faire, le temps que le relevé suive.
 */
export interface AudienceModeration {
  readonly gestures: ModerationGestures;
  /** Ce que l'interprète sait de ses propres gestes, par identité. */
  readonly mutedOverride: ReadonlyMap<string, boolean>;
}

export function RecitalAudience({
  identity,
  lines,
  headcount,
  paused,
  moderation,
}: {
  identity: string | null;
  /** Le dernier relevé du service, `null` tant qu'aucun n'a abouti. */
  lines: readonly LiveParticipantLine[] | null;
  /** Combien le service voit de monde, soi compris. */
  headcount: number;
  /** La séance est-elle en pause ; `null` tant qu'aucun état n'est lu. */
  paused: boolean | null;
  /** Chez l'interprète seul. */
  moderation?: AudienceModeration;
}) {
  const performer = lines?.find((line) => line.canPlay) ?? null;

  return (
    <div className="flex flex-col gap-4">
      <PerformerCard
        loading={lines === null}
        performer={performer}
        isMe={performer !== null && performer.identity === identity}
        paused={paused}
      />

      <section className="flex flex-col gap-2" aria-labelledby="recital-audience-title">
        <header className="flex items-baseline justify-between gap-2">
          <h3
            id="recital-audience-title"
            className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted/70"
          >
            Dans la salle
          </h3>

          {lines !== null && (
            <span className="text-[11px] tabular-nums text-text-muted">
              {audienceCountOf(lines, headcount)}
            </span>
          )}
        </header>

        <AudienceList identity={identity} lines={lines} headcount={headcount} moderation={moderation} />
      </section>
    </div>
  );
}

/**
 * Les spectateurs seuls, sans fiche d'interprète ni intertitre.
 *
 * Extraite pour le widget du rouleau (`recital-widgets.tsx`), où le cadre porte
 * déjà le titre et le compte, et où l'interprète, c'est soi : sa propre fiche
 * ne lui apprendrait rien, et son état — joue, en pause — se lit déjà dans la
 * barre. Deux écritures de la même liste divergeraient au premier ajustement.
 */
export function AudienceList({
  identity,
  lines,
  headcount,
  moderation,
}: {
  identity: string | null;
  lines: readonly LiveParticipantLine[] | null;
  headcount: number;
  moderation?: AudienceModeration;
}) {
  const audience = sortAudience((lines ?? []).filter((line) => !line.canPlay), identity);
  // La liste est plafonnée par le service ; le compte, lui, est le vrai total.
  const hidden = Math.max(0, audienceCountOf(lines, headcount) - audience.length);

  return (
    <PeopleList
      identity={identity}
      people={lines === null ? null : audience}
      hidden={hidden}
      empty="Personne dans la salle pour l'instant. Envoie le lien du récital."
      moderation={moderation}
    />
  );
}

/**
 * Des personnes, une par ligne : une initiale dans un disque, le nom, et ce
 * qu'il y a à dire d'elles.
 *
 * Le dessin de « La salle » d'un récital, partagé depuis le 2026-09-23 avec la
 * séance à plusieurs (`jam-widget.tsx`) pour que les deux salles se lisent
 * pareil. Chacune choisit qui y figure — les spectateurs ici, les autres
 * joueurs là-bas —, et ce qui se dit à droite du nom : rien dans un récital, la
 * latence de chacun quand on joue ensemble.
 */
export function PeopleList({
  identity,
  people,
  hidden = 0,
  empty,
  moderation,
  trailing,
}: {
  identity: string | null;
  /** Qui figure dans la liste, dans l'ordre ; `null` tant que rien n'est relevé. */
  people: readonly LiveParticipantLine[] | null;
  /** Combien d'autres, au-delà de ce que le relevé a rendu. */
  hidden?: number;
  /** Ce que dit une liste vide — et ce qu'on peut y faire. */
  empty: string;
  moderation?: AudienceModeration;
  /** À droite du nom. */
  trailing?: (line: LiveParticipantLine) => ReactNode;
}) {
  return (
    <>
      {people === null && <AudienceSkeleton />}

      {people !== null && people.length === 0 && (
        <p className="rounded-lg border border-dashed border-border-subtle px-3 py-3 text-[12px] leading-[1.45] text-text-muted">
          {empty}
        </p>
      )}

      {people !== null && people.length > 0 && (
        <ul className="flex flex-col">
          {people.map((line) => {
            const muted = moderation?.mutedOverride.get(line.identity) ?? line.muted;

            return (
              <li key={line.identity} className="flex items-center gap-2.5 rounded-md px-1.5 py-1.5 hover:bg-white/[0.03]">
                <Initial name={line.name} size={24} />

                <span className="min-w-0 flex-1 truncate text-[13px] text-text-secondary">{line.name}</span>

                {line.identity === identity && <MeTag />}

                {trailing?.(line)}

                {muted && <MutedTag />}

                {moderation !== undefined && line.identity !== identity && (
                  <ModerationMenu
                    target={{ identity: line.identity, name: line.name, muted }}
                    gestures={moderation.gestures}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hidden > 0 && (
        <p className="px-1.5 text-[12px] text-text-muted">
          et {hidden} autre{hidden > 1 ? "s" : ""}
        </p>
      )}
    </>
  );
}

/**
 * Combien de monde dans la salle, l'interprète non compris.
 *
 * Le relevé du service est plafonné ; le compte de salle, lui, est entier. On
 * garde le plus grand des deux, faute de quoi une salle pleine s'annoncerait
 * au nombre de noms que le service a bien voulu rendre.
 */
export function audienceCountOf(
  lines: readonly LiveParticipantLine[] | null,
  headcount: number,
): number {
  const seated = (lines ?? []).filter((line) => !line.canPlay).length;
  const onStage = (lines ?? []).some((line) => line.canPlay);

  return Math.max(seated, headcount - (onStage ? 1 : 0));
}

/** L'interprète, en tête : son nom, et s'il joue. */
function PerformerCard({
  loading,
  performer,
  isMe,
  paused,
}: {
  loading: boolean;
  performer: LiveParticipantLine | null;
  isMe: boolean;
  paused: boolean | null;
}) {
  if (loading) {
    return <div className="h-[62px] rounded-xl border border-border-subtle bg-white/[0.02] animate-pulse" />;
  }

  return (
    <section
      aria-label="L'interprète"
      className="flex items-center gap-3 rounded-xl border border-border-subtle bg-gradient-to-br from-accent-subtle/60 to-transparent px-3 py-2.5"
    >
      {performer === null ? (
        <span className="w-9 h-9 grid place-items-center rounded-full border border-dashed border-border-strong text-[13px] text-text-muted">
          ?
        </span>
      ) : (
        <Initial name={performer.name} size={36} accent />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="truncate text-[14px] font-medium text-text-primary">
            {performer === null ? "L'interprète" : performer.name}
          </span>

          {isMe && <MeTag />}
        </div>

        <span className="text-[11px] text-text-muted">au piano</span>
      </div>

      <PerformerState present={performer !== null} paused={paused} />
    </section>
  );
}

/** « joue », « en pause » ou « pas encore là », en pastille. */
function PerformerState({ present, paused }: { present: boolean; paused: boolean | null }) {
  if (!present) {
    return <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] text-text-muted bg-white/[0.04]">pas encore là</span>;
  }

  if (paused === true) {
    return <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] text-amber-300/90 bg-amber-500/10">en pause</span>;
  }

  return (
    <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] text-red-300 bg-red-500/10">
      <span aria-hidden="true" className="relative flex w-1.5 h-1.5">
        <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60 motion-safe:animate-ping" />
        <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-red-400" />
      </span>
      joue
    </span>
  );
}

/**
 * Une initiale dans un disque.
 *
 * La teinte vient du nom : deux spectateurs qui partagent une initiale se
 * distinguent encore d'un coup d'œil, et chacun garde sa couleur d'un relevé à
 * l'autre. L'interprète garde l'accent de la salle d'attente.
 */
function Initial({ name, size, accent = false }: { name: string; size: number; accent?: boolean }) {
  const hue = hueOf(name);

  return (
    <span
      aria-hidden="true"
      className={`shrink-0 grid place-items-center rounded-full font-semibold ${accent ? "bg-accent-subtle text-accent-bright" : ""}`}
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.42),
        ...(accent ? {} : { background: `hsl(${hue} 55% 55% / 0.16)`, color: `hsl(${hue} 70% 78%)` }),
      }}
    >
      {initialOf(name)}
    </span>
  );
}

function MeTag() {
  return (
    <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium text-accent-bright bg-accent-subtle">
      toi
    </span>
  );
}

function AudienceSkeleton() {
  return (
    <ul className="flex flex-col gap-1" aria-hidden="true">
      {[0, 1, 2].map((row) => (
        <li key={row} className="flex items-center gap-2.5 px-1.5 py-1.5">
          <span className="w-6 h-6 rounded-full bg-white/[0.05] animate-pulse" />
          <span className="h-3 rounded bg-white/[0.05] animate-pulse" style={{ width: `${55 - row * 12}%` }} />
        </li>
      ))}
    </ul>
  );
}

/** Soi d'abord, puis par nom : on se cherche en premier dans une liste. */
export function sortAudience(lines: readonly LiveParticipantLine[], identity: string | null): LiveParticipantLine[] {
  return [...lines].sort((a, b) => {
    if (a.identity === identity) {
      return -1;
    }

    if (b.identity === identity) {
      return 1;
    }

    return a.name.localeCompare(b.name, "fr", { sensitivity: "base" });
  });
}

function hueOf(name: string): number {
  let hash = 0;

  for (const char of name) {
    hash = (hash * 31 + (char.codePointAt(0) ?? 0)) % 360;
  }

  return hash;
}
